import dayjs from 'dayjs';

import {
  ICreatePromoCampaignRequest,
  ICreatePromoCodeRequest,
  PromoCampaignRequestInput,
} from 'schemas';
import { EPromoScopeType, EPromoValueType } from 'types';

import {
  ETabsModal,
  IChosenClientItem,
  ISubmitPromoParams,
} from './interfaces';

export const buildApplicableParams = (
  chosenPositions: Array<{
    id: number;
    title: string;
    type?: ETabsModal;
  }>
): ICreatePromoCodeRequest['input']['applicable_params'] => {
  if (!chosenPositions.length) return undefined;

  const result: ICreatePromoCodeRequest['input']['applicable_params'] = {};

  chosenPositions.forEach((p) => {
    if (!p.type) return;

    if (p.id === -1) {
      result[p.type.toLowerCase() as 'goods' | 'services' | 'tariffs'] = [-1];
    } else {
      if (!result[p.type.toLowerCase() as 'goods' | 'services' | 'tariffs']) {
        result[p.type.toLowerCase() as 'goods' | 'services' | 'tariffs'] = [
          p.id,
        ];
      } else {
        result[p.type.toLowerCase() as 'goods' | 'services' | 'tariffs']?.push(
          p.id
        );
      }
    }
  });

  return result;
};

export const buildClientScopeParams = (
  chosenItems: Array<IChosenClientItem>
): ICreatePromoCodeRequest['input']['client_scope'] => {
  if (!chosenItems.length) return undefined;

  const result: { groups: Array<string>; users: Array<string> } = {
    groups: [],
    users: [],
  };

  chosenItems.forEach(({ type, id }) => {
    if (type === 'client') result.users.push(id);
    if (type === 'group') result.groups.push(id);
  });

  return result;
};

export const normaliseSubmitPromoInput = ({
  values,
  chosenItems,
  chosenPositions,
  areas,
  rewardType,
}: ISubmitPromoParams): PromoCampaignRequestInput => {
  const input: ICreatePromoCampaignRequest['input'] = {
    title: values.title,
    value: {
      type: values.rewardType as EPromoValueType,
      value: Number(values.rewardValue),
    },
    active: values.active,
    is_tracked: values.is_tracked,
    generation_settings: {
      amount: values.codesCount ? values.codesCount : undefined,
      length: values.codeLength ? values.codeLength : undefined,
      prefix: values.prefix ? values.prefix : undefined,
      type: values.format ? values.format : undefined,
    },
  };

  const applicable_params =
    rewardType === EPromoValueType.DISCOUNT
      ? buildApplicableParams(chosenPositions)
      : null;

  const scopes: Array<EPromoScopeType> | undefined =
    areas
      .filter((area) => area.active)
      .map((area) => area.value as EPromoScopeType) || undefined;

  const client_scope = buildClientScopeParams(chosenItems);

  if (applicable_params) {
    input.applicable_params = applicable_params;
  } else {
    input.applicable_params = null;
  }

  if (scopes?.length) {
    input.scopes = scopes;
  }

  if (values.amount) {
    input.amount = values.amount;
  }

  if (client_scope) {
    input.client_scope = client_scope;
  } else {
    input.client_scope = null;
  }

  if (values.activeFrom) {
    input.active_from = dayjs(values.activeFrom).format('YYYY-MM-DD HH:mm:ss');
  } else {
    input.active_from = null;
  }

  if (values.activeTo) {
    input.active_to = dayjs(values.activeTo).format('YYYY-MM-DD HH:mm:ss');
  } else {
    input.active_to = null;
  }

  return input;
};
